import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, ArrowRight, Eye } from 'lucide-react';

interface ExamResult {
  id: string;
  date: string;
  section: string;
  score: number;
  passed: boolean;
  durationMinutes: number;
}

interface RecentExamsTableProps {
  exams: ExamResult[];
}

export const RecentExamsTable: React.FC<RecentExamsTableProps> = ({ exams }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRowClick = (exam: ExamResult) => {
    navigate(`/results?attemptId=${exam.id}`);
  };

  if (!exams.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('dashboard.recentExamsTable.noExams')}</p>
        <p className="text-sm text-gray-400 mt-1">{t('dashboard.recentExamsTable.startPracticing')}</p>
        <button
          onClick={() => navigate('/exam-selection')}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          {t('dashboard.recentExamsTable.startFirstExam')}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recentExamsTable.title')}</h2>
          <p className="text-sm text-gray-500">{t('dashboard.recentExamsTable.subtitle')}</p>
        </div>
        <Link 
          to="/exam-history" 
          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
        >
          {t('dashboard.recentExamsTable.viewAllResults')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tl-lg">
                {t('dashboard.recentExamsTable.date')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('dashboard.recentExamsTable.section')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('dashboard.recentExamsTable.score')}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('dashboard.recentExamsTable.status')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('dashboard.recentExamsTable.duration')}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tr-lg">
                {t('dashboard.recentExamsTable.action')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {exams.map((exam) => (
              <tr 
                key={exam.id} 
                className="hover:bg-blue-50 transition-colors cursor-pointer"
                onClick={() => handleRowClick(exam)}
              >
                <td className="px-6 py-4 text-sm text-gray-900">
                  {new Date(exam.date).toLocaleString('nl-NL', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                  {exam.section}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <span className={`font-bold ${exam.passed ? 'text-success' : 'text-error'}`}>
                    {exam.score}%
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-center">
                  {exam.passed ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-success/10 text-success text-xs font-medium">
                      <CheckCircle className="w-3.5 h-3.5" />
                      {t('dashboard.recentExamsTable.passed')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-error/10 text-error text-xs font-medium">
                      <XCircle className="w-3.5 h-3.5" />
                      {t('dashboard.recentExamsTable.notYet')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-right text-gray-600">
                  {exam.durationMinutes} min
                </td>
                <td className="px-6 py-4 text-sm text-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(exam);
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    {t('dashboard.recentExamsTable.view')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sm:hidden space-y-3">
        {exams.map((exam) => (
          <button 
            key={exam.id} 
            onClick={() => handleRowClick(exam)}
            className="w-full bg-gray-50 rounded-xl p-4 flex items-center justify-between hover:bg-blue-50 transition-colors text-left"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-gray-900">{exam.section}</span>
                {exam.passed ? (
                  <CheckCircle className="w-4 h-4 text-success" />
                ) : (
                  <XCircle className="w-4 h-4 text-error" />
                )}
              </div>
              <p className="text-xs text-gray-500">
                {new Date(exam.date).toLocaleString('nl-NL', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })} · {exam.durationMinutes} min
              </p>
            </div>
            <div className="text-right flex items-center gap-3">
              <span className={`text-xl font-bold ${exam.passed ? 'text-success' : 'text-error'}`}>
                {exam.score}%
              </span>
              <ArrowRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
